/**
 * Place lookup ver 2.0 with:
 * - added permission check for create
 * - lookup by place name, PlaceID and address
 * - handle multiple places
 *
 * To set default value pass ptsPlaceID prop to component or [ptsPlaceID, ...] for multiple
 * For some reason in multiple mode (+) button is not showing up
 */

import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import store from 'config/configureStore';
import { showEditPlace } from 'reducers/DialogsReducer';
import { IconButton, TextField } from '@material-ui/core';
import { AddIcon } from 'evergreen-ui';
import { getFullPermissions } from 'reducers/PermissionsReducer';

const getService = (serviceName = 'cad') => {
  let service;
  try {
    const state = store.store.getState();
    const client = state.websocket.websocket;
    service = client.service(serviceName);
    service.timeout = 20000;
  } catch (err) {
    throw new Error('Error, service not ready.');
  }
  return service;
};

const useStyles = makeStyles((theme) => ({
  textField: {
    margin: '0 4px 8px',
  },
  activeWarrant: {
    background: theme.palette.error.main,
    color: theme.card.bg,
    width: 'calc(100% + 32px)',
    padding: '8px 16px',
    margin: '-8px -16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 14,
    '& em': {
      fontStyle: 'normal',
      fontSize: 12,
    },
  },
  option: {
    width: '100%',
    '& > div:first-of-type': {
      fontSize: 14,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      fontWeight: 500,
      '& em': {
        fontStyle: 'normal',
        fontSize: 12,
        fontWeight: 400,
      },
    },
    '& > div:last-of-type': {
      fontSize: 12,
      opacity: 0.9,
    },
  },
}));

const btnStyle = {
  margin: '0 8px 0 2px',
  padding: 0,
};

function PlaceLookup(props) {
  const { onChange, ptsPlaceID, dialogs, multiple, onReset } = props;
  const classes = useStyles();
  const [searchMatch, setSearchMatch] = useState([]);
  const [Value, setValue] = useState(multiple ? [] : null);
  const label = props.label ? props.label : 'Place Lookup';
  const mountedRef = useRef(true);
  const throttleRef = useRef(0);
  const addEnabled = dialogs.editPlace === null;
  const placesPermission = getFullPermissions('globals', 'Places', 'any');
  const canCreate = placesPermission.Create;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setDefaultValue();
    // eslint-disable-next-line
  }, [ptsPlaceID]);

  const onSearchSelect = async (ev, selection) => {
    if (!selection || (multiple && selection.length === 0)) {
      setValue(multiple ? [] : null);
      if (onReset) {
        onReset();
      } else {
        onChange(multiple ? [] : null);
      }
      return;
    }
    if (multiple) {
      const ptsPlaceIDs = selection.map((p) => p.ptsPlaceID);
      const newPlaces = await getPlacesDetails(ptsPlaceIDs);
      setValue(newPlaces);
      onChange(newPlaces);
    } else {
      const place = await getPlaceDetails(selection);
      setValue(selection);
      onChange(place);
    }
  };

  const getOptionSelected = (option, value) => {
    const found = searchMatch.find((p) => p.ptsPlaceID === value.ptsPlaceID);
    if (found) {
      return option.ptsPlaceID === value.ptsPlaceID;
    } else {
      return true;
    }
  };

  const renderOption = (option) => {
    const { PlaceName, PlaceID = '', FullAddressText = '' } = option;
    return (
      <div className={classes.option}>
        <div>
          <span>{PlaceName} </span>
          <em>{PlaceID}</em>
        </div>
        <div>{FullAddressText}</div>
      </div>
    );
  };

  const onInputChange = (ev, val) => {
    clearTimeout(throttleRef.current);
    throttleRef.current = setTimeout(() => {
      if (val.length > 2) searchPlace(val);
    }, 1000);
  };

  const searchPlace = async (search) => {
    try {
      const service = getService('place-lookup');
      const result = await service.find({ query: { search } });
      if (!mountedRef.current) return;
      if (result) {
        setSearchMatch(result);
      }
    } catch (err) {
      console.log('Place lookup error 1: ', err);
    }
  };

  const getPlaceDetails = async (selection) => {
    const { ptsPlaceID, ptsAddressID = null } = selection;
    if (!ptsPlaceID) return selection;
    try {
      const service = getService('place-lookup');
      const place = await service.get(ptsPlaceID, { query: { ptsAddressID } });
      if (!mountedRef.current) return;
      return place;
    } catch (err) {
      return selection;
    }
  };

  const getPlacesDetails = async (ptsPlaceIDs) => {
    if (!ptsPlaceIDs) return [];
    try {
      const service = getService('place-lookup');
      const places = await service.get(1, { query: { ptsPlaceIDs } });
      if (!mountedRef.current) return [];
      return places;
    } catch (err) {
      return [];
    }
  };

  const setDefaultValue = async () => {
    if (!ptsPlaceID) return;
    const service = getService('place-lookup');
    try {
      if (multiple) {
        if (!Array.isArray(ptsPlaceID) || !arePlacesChanged()) return;
        let places = await service.get(1, { query: { ptsPlaceIDs: ptsPlaceID } });
        setValue(places);
      } else {
        if (typeof ptsPlaceID !== 'number') return;
        const place = await service.get(ptsPlaceID);
        setValue(place);
      }
    } catch (err) {
      console.log('Place lookup error: ', err);
    }
  };

  const arePlacesChanged = () => {
    const original = ptsPlaceID.sort().toString();
    const current = Value.map((p) => p.ptsPlaceID)
      .sort()
      .toString();
    return original !== current;
  };

  const onAdd = () => {
    props.showEditPlace();
  };

  const startAdornment =
    addEnabled && canCreate ? (
      <IconButton style={btnStyle} onClick={onAdd}>
        <AddIcon fontSize="small" />
      </IconButton>
    ) : undefined;

  const renderInput = (params) => {
    params.label = label;
    params.variant = 'outlined';
    return multiple ? (
      <TextField
        {...params}
        label={label}
        variant="outlined"
        placeholder=""
        size="small"
        fullWidth
        autoComplete="off"
        inputProps={{
          ...params.inputProps,
          autoComplete: 'off',
        }}
      />
    ) : (
      <TextField
        {...params}
        InputProps={{
          ...params.InputProps,
          autoComplete: 'off',
          startAdornment,
        }}
      />
    );
  };

  return (
    <Autocomplete
      multiple={multiple}
      disabled={props.disabled}
      className={props.className}
      fullWidth
      autoHighlight
      autoSelect={false}
      options={searchMatch}
      value={Value}
      getOptionLabel={(option) => option.PlaceName}
      style={props.style}
      size="small"
      onChange={onSearchSelect}
      onInputChange={onInputChange}
      renderInput={renderInput}
      getOptionSelected={getOptionSelected}
      renderOption={renderOption}
      filterOptions={(option) => option}
      disableCloseOnSelect={Boolean(multiple)}
      limitTags={15}
    />
  );
}

const mapStateToProps = (state) => ({
  dialogs: state.dialogs,
  dataUpdate: state.dataUpdate,
});

export default connect(mapStateToProps, { showEditPlace })(PlaceLookup);
